import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useOutletContext } from 'react-router-dom';
import { parsePhoneNumberFromString } from 'libphonenumber-js';

import Fader from '../../components/Loaders/Fader';
import Switcher from '../../components/Forms/Switcher/Switcher';
import CustomSelect from '../../components/Forms/CustomSelect/CustomSelect';
import InputItem from '../../components/Forms/InputItem/InputItem';
import EmailSettingItem from '../../components/EmailSettingItem/EmailSettingItem';
import VerifyModal from '../../components/VerifyModal/VerifyModal';

import {
  AUTH_METHOD_TYPES,
  NOTIFICATIONS_FREQUENCY_TYPES,
  PROTOCOL_TYPES,
  PROVIDER_TYPES,
  USER_DATA,
  USER_ROLES,
} from '../../../util/constants/AppConstants';
import {
  getEmailSettings,
  getSettings,
  saveAuthSettings,
  saveEmailSettings,
  saveNotificationsSettings,
  saveProfileSettings,
} from '../../../http/userAPI';
import { setNotify } from '../../../datastore/actions/actionActions';
import { saveCustomState } from '../../../util/LocalStorage';
import { sendSmsVerificationCode, validateSmsVerificationCode } from '../../../http/authAPI';
import { maskPhone } from '../../../util/HelperFunctions';

import PlusIcon from '../../../assets/images/icons/plus.svg';
import './Settings.scss';

const Settings = () => {
  const [notificationsSettingsLoading, setNotificationsSettingsLoading] = useState(false);
  const [emailSettingsLoading, setEmailSettingsLoading] = useState(false);
  const [phone, setPhone] = useState('');
  const [isVerificationOpen, setIsVerificationOpen] = useState(false);
  const [verificationLoading, setVerificationLoading] = useState(false);
  const [authSettings, setAuthSettings] = useState('email');
  const [notificationsSettings, setNotificationsSettings] = useState({
    email: false,
    sms: false,
    frequency: 'Daily',
    xMessages: null,
  });
  const [autoMessagesApproval, setAutoMessagesApproval] = useState(false);
  const [initialEmailSettings, setInitialEmailSettings] = useState([
    {
      type: PROTOCOL_TYPES[0].value,
      provider: PROVIDER_TYPES[4].value,
      mailServer: '',
      email: '',
      password: '',
      verified: false,
    },
  ]);
  const [emailSettings, setEmailSettings] = useState([
    {
      type: PROTOCOL_TYPES[0].value,
      provider: PROVIDER_TYPES[4].value,
      mailServer: '',
      email: '',
      password: '',
      verified: false,
    },
  ]);
  const [profileSavingLoading, setProfileSavingLoading] = useState(false);
  const [authSavingLoading, setAuthSavingLoading] = useState(false);
  const [notificationsSavingLoading, setNotificationsSavingLoading] = useState(false);
  const [emailSavingLoading, setEmailSavingLoading] = useState(false);

  const config = useSelector((state) => state.config.config);

  const dispatch = useDispatch();

  const { userInfo, setUserInfo } = useOutletContext();

  const managerRoleValue = USER_ROLES.find((role) => role.short === 'Manager').value;
  const mppRoleValue = USER_ROLES.find((role) => role.short === 'MPP').value;
  const councillorRoleValue = USER_ROLES.find((role) => role.short === 'Councillor').value;

  const isManager = userInfo && userInfo.role === managerRoleValue;
  const isMpp = userInfo && userInfo.role === mppRoleValue;
  const isCouncillor = userInfo && userInfo.role === councillorRoleValue;

  useEffect(() => {
    if (userInfo) {
      setPhone(`+${userInfo.phone}`);
      setAuthSettings(userInfo.auth_method);
    }
  }, [userInfo]);

  const handleGetSettings = async (userId) => {
    setNotificationsSettingsLoading(true);

    const res = await getSettings(config, userId);

    if (res.status === 200) {
      setNotificationsSettings({
        email: Boolean(res.data.settings.email_notifications),
        sms: Boolean(res.data.settings.sms_notifications),
        frequency: res.data.settings.frequency,
        xMessages: res.data.settings.x_messages,
      });

      setNotificationsSettingsLoading(false);
    } else {
      console.log('ERROR: Grabbing Settings');
    }
  };

  const handleGetEmailSettings = async (userId) => {
    setEmailSettingsLoading(true);

    const res = await getEmailSettings(config, userId);

    if (res.status === 200) {
      const settings = res.data.emailSettings.map((item) => {
        if (item.email) {
          return item;
        }

        return {
          type: PROTOCOL_TYPES[0].value,
          provider: PROVIDER_TYPES[4].value,
          mailServer: '',
          email: '',
          password: '',
          verified: false,
        };
      });

      setAutoMessagesApproval(res.data.autoMessagesApproval);
      setEmailSettings(settings);
      setInitialEmailSettings(settings.map((item) => ({ ...item })));

      setEmailSettingsLoading(false);
    } else {
      console.log('ERROR: Grabbing Email Settings');
    }
  };

  useEffect(() => {
    if (isMpp || isCouncillor) {
      handleGetSettings(userInfo.id);
    }

    if (isManager || isMpp || isCouncillor) {
      handleGetEmailSettings(userInfo.id);
    }
  }, [userInfo]);

  const handlePhoneChange = (e) => {
    let { value } = e.target;

    const digits = value.replace(/\D/g, '');

    if (digits.length === 0) {
      value = '';
    } else {
      value = `+${digits}`;
    }

    setPhone(value);
  };

  const handleSaveProfileSettings = async () => {
    if (phone !== `+${userInfo.phone}`) {
      if (!phone) {
        dispatch(
          setNotify({
            status: true,
            type: 'error',
            title: 'Error',
            message: 'Please enter a phone number',
          }),
        );
        return;
      }

      const parsed = parsePhoneNumberFromString(phone);
      if (!parsed || !parsed.isValid()) {
        dispatch(
          setNotify({
            status: true,
            type: 'error',
            title: 'Error',
            message: 'Please enter a valid phone number',
          }),
        );
        return;
      }

      setProfileSavingLoading(true);

      const res = await sendSmsVerificationCode(config, phone);
      if (res.status !== 200 || !res.data.success) {
        dispatch(
          setNotify({
            status: true,
            type: 'error',
            title: 'Error',
            message: 'Verification code could not be sent. Please try again later',
          }),
        );
      }

      setIsVerificationOpen(true);
    } else {
      dispatch(
        setNotify({
          status: true,
          type: 'success',
          title: 'Success',
          message: 'Profile settings have been successfully changed',
        }),
      );
    }
  };

  const handleVerificationResend = async () => {
    const res = await sendSmsVerificationCode(config, phone);

    if (res.status === 200 && res.data.success) {
      dispatch(
        setNotify({
          status: true,
          type: 'success',
          title: 'Success',
          message: 'A new SMS verification code has been sent.',
        }),
      );
    } else {
      dispatch(
        setNotify({
          status: true,
          type: 'error',
          title: 'Error',
          message: 'Verification code could not be sent. Please try again later',
        }),
      );
    }
  };

  const completeProfileSettingsSaving = async (phone) => {
    setIsVerificationOpen(false);

    const res = await saveProfileSettings(config, userInfo.id, phone.replace(/^\+/, ''));

    if (res.status === 200) {
      const updatedUser = {
        ...userInfo,
        phone: phone.replace(/^\+/, ''),
      };
      saveCustomState(USER_DATA, updatedUser, true);
      setUserInfo(updatedUser);

      dispatch(
        setNotify({
          status: true,
          type: 'success',
          title: 'Success',
          message: res.data.message,
        }),
      );
    } else {
      dispatch(
        setNotify({
          status: true,
          type: 'error',
          title: 'Error',
          message: res.message,
        }),
      );
    }
    setProfileSavingLoading(false);
  };

  const handleValidateVerification = async (code) => {
    const userCode = Number(String(code).replace(/,/g, ''));

    const res = await validateSmsVerificationCode(config, phone, userCode);

    if (res.status === 200) {
      if (!res.data.success) {
        dispatch(
          setNotify({
            status: true,
            type: 'error',
            title: 'Error',
            message: res.data.message,
          }),
        );
        setVerificationLoading(false);
        return;
      }

      completeProfileSettingsSaving(phone);
    } else {
      dispatch(
        setNotify({
          status: true,
          type: 'error',
          title: 'Error',
          message: 'Verification code could not be verified. Please try again later',
        }),
      );
      setVerificationLoading(false);
    }
  };

  const handleSaveNotificationsSettings = async () => {
    if (
      notificationsSettings.frequency &&
      (notificationsSettings.frequency !== 'After X messages' ||
        (notificationsSettings.frequency === 'After X messages' && notificationsSettings.xMessages))
    ) {
      setNotificationsSavingLoading(true);

      const res = await saveNotificationsSettings(
        config,
        userInfo.id,
        notificationsSettings.email,
        notificationsSettings.sms,
        notificationsSettings.frequency,
        notificationsSettings.frequency === 'After X messages' ? notificationsSettings.xMessages : null,
      );
      if (res.status === 200) {
        dispatch(
          setNotify({
            status: true,
            type: 'success',
            title: 'Success',
            message: res.data.message,
          }),
        );
      } else {
        dispatch(
          setNotify({
            status: true,
            type: 'error',
            title: 'Error',
            message: res.message,
          }),
        );
      }
      setNotificationsSavingLoading(false);
    } else {
      dispatch(
        setNotify({
          status: true,
          type: 'error',
          title: 'Error',
          message: 'Fill in all fields',
        }),
      );
    }
  };

  const addEmail = () => {
    setEmailSettings((prev) => [
      ...prev,
      {
        type: PROTOCOL_TYPES[0].value,
        provider: PROVIDER_TYPES[4].value,
        mailServer: '',
        email: '',
        password: '',
        verified: false,
      },
    ]);
  };

  const areAllEmailsFilled = () => {
    return emailSettings.every(
      (elem) =>
        elem.email.trim() !== '' &&
        (['Gmail', 'Outlook', 'Office 365'].includes(elem.provider) ||
          (!['Gmail', 'Outlook', 'Office 365'].includes(elem.provider) && elem.password.trim() !== '')) &&
        (elem.provider !== 'Other' || (elem.provider === 'Other' && elem.mailServer.trim() !== '')),
    );
  };

  const handleSaveEmailSettings = async () => {
    if (areAllEmailsFilled()) {
      setEmailSavingLoading(true);

      const res = await saveEmailSettings(config, userInfo.id, emailSettings, autoMessagesApproval);
      if (res.status === 200) {
        dispatch(
          setNotify({
            status: true,
            type: 'success',
            title: 'Success',
            message: res.data.message,
          }),
        );
        handleGetEmailSettings(userInfo.id);
      } else {
        dispatch(
          setNotify({
            status: true,
            type: 'error',
            title: 'Error',
            message: res.message,
          }),
        );
      }
      setEmailSavingLoading(false);
    } else {
      dispatch(
        setNotify({
          status: true,
          type: 'error',
          title: 'Error',
          message: 'Fill in all fields',
        }),
      );
    }
  };

  const handleSaveAuthSettings = async () => {
    setAuthSavingLoading(true);

    const res = await saveAuthSettings(config, userInfo.id, authSettings);
    if (res.status === 200) {
      const updatedUser = {
        ...userInfo,
        auth_method: authSettings,
      };
      saveCustomState(USER_DATA, updatedUser, true);
      setUserInfo(updatedUser);

      dispatch(
        setNotify({
          status: true,
          type: 'success',
          title: 'Success',
          message: res.data.message,
        }),
      );
    } else {
      dispatch(
        setNotify({
          status: true,
          type: 'error',
          title: 'Error',
          message: res.message,
        }),
      );
    }
    setAuthSavingLoading(false);
  };

  return (
    <div className="settings">
      <div className="settings__block">
        <h2 className="settings__title">Profile settings</h2>
        {!userInfo ? (
          <Fader />
        ) : (
          <div className="settings__notifications">
            <InputItem label="Phone number" type="text" onChange={handlePhoneChange} value={phone} />
          </div>
        )}
        <div className="settings__btn-wrap">
          {profileSavingLoading ? (
            <Fader />
          ) : (
            <div className="settings__btn create-btn secondary" onClick={handleSaveProfileSettings}>
              Save
            </div>
          )}
        </div>
      </div>
      <div className="settings__block">
        <h2 className="settings__title">Authentication settings</h2>
        <p className="settings__text">Choose how you want to receive your verification code when logging in.</p>
        {!userInfo || !userInfo.auth_method ? (
          <Fader />
        ) : (
          <div className="settings__notifications">
            <CustomSelect
              label="Code delivery method"
              options={AUTH_METHOD_TYPES}
              selectedOption={authSettings}
              setSelectedOption={setAuthSettings}
            />
          </div>
        )}
        <div className="settings__btn-wrap">
          {authSavingLoading ? (
            <Fader />
          ) : (
            <div className="settings__btn create-btn secondary" onClick={handleSaveAuthSettings}>
              Save
            </div>
          )}
        </div>
      </div>
      {(isMpp || isCouncillor) && (
        <div className="settings__block">
          <h2 className="settings__title">Notifications settings</h2>
          {notificationsSettingsLoading ? (
            <Fader />
          ) : (
            <div className="settings__notifications">
              <div className="settings__switcher">
                <span>Email notifications</span>
                <Switcher
                  isChecked={notificationsSettings.email}
                  setIsChecked={(value) => setNotificationsSettings((prev) => ({ ...prev, email: value }))}
                />
              </div>
              <div className="settings__switcher">
                <span>SMS notifications</span>
                <Switcher
                  isChecked={notificationsSettings.sms}
                  setIsChecked={(value) => setNotificationsSettings((prev) => ({ ...prev, sms: value }))}
                />
              </div>
              <CustomSelect
                label="Frequency"
                options={NOTIFICATIONS_FREQUENCY_TYPES}
                selectedOption={notificationsSettings.frequency}
                setSelectedOption={(value) => setNotificationsSettings((prev) => ({ ...prev, frequency: value }))}
              />
              {notificationsSettings.frequency === 'After X messages' && (
                <InputItem
                  label="Message count"
                  type="number"
                  onChange={(e) => setNotificationsSettings((prev) => ({ ...prev, xMessages: e.target.value }))}
                  value={notificationsSettings.xMessages}
                />
              )}
            </div>
          )}
          <div className="settings__btn-wrap">
            {notificationsSavingLoading ? (
              <Fader />
            ) : (
              <div className="settings__btn create-btn secondary" onClick={handleSaveNotificationsSettings}>
                Save
              </div>
            )}
          </div>
        </div>
      )}
      {(isManager || isMpp || isCouncillor) && (
        <div className="settings__block">
          <h2 className="settings__title">Email Server Settings</h2>
          {emailSettingsLoading ? (
            <Fader />
          ) : (
            <>
              <div className="settings__notifications">
                <div className="settings__switcher">
                  <span>Auto approve email ingestion</span>
                  <Switcher isChecked={autoMessagesApproval} setIsChecked={setAutoMessagesApproval} />
                </div>
              </div>
              <p className="settings__text">
                When turned on, will automatically approve all incoming email inbox messages into system, bypassing the
                manual approval process
              </p>
              <div className="settings__emails">
                {emailSettings.map((elem, index) => (
                  <EmailSettingItem
                    key={index}
                    email={elem}
                    index={index}
                    initialEmailSettings={initialEmailSettings}
                    emailSettings={emailSettings}
                    setEmailSettings={setEmailSettings}
                  />
                ))}
                <div className="settings__add" onClick={addEmail}>
                  <img src={PlusIcon} />
                </div>
              </div>
            </>
          )}
          <div className="settings__btn-wrap">
            {emailSavingLoading ? (
              <Fader />
            ) : (
              <div className="settings__btn create-btn secondary" onClick={handleSaveEmailSettings}>
                Save
              </div>
            )}
          </div>
        </div>
      )}
      {isVerificationOpen && (
        <VerifyModal
          isOpen={isVerificationOpen}
          setIsOpen={setIsVerificationOpen}
          validation={handleValidateVerification}
          resend={handleVerificationResend}
          setLoginChallenge={setProfileSavingLoading}
          maskedPhone={maskPhone(phone)}
          default2faMethod="sms"
          loading={verificationLoading}
          setLoading={setVerificationLoading}
          isLogin={false}
        />
      )}
    </div>
  );
};

export default Settings;
